import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { Button, Typography } from 'ui';
import { Form, Formik } from 'formik';
import {
  MetroAreaContainerWidgetFields,
  MetroAreaContainerWidgetFormFields,
  invalidMetroWidgetItemTypes,
} from './MetroAreaContainerWidgetFormFields';
import { StoreType } from 'utils/connect';
import { consumer } from '@tock/coretypes';
import { fetchAll as fetchAllMetroAreas } from 'actions/MetroAreaActions';
import { fetchAllNamedConfigurations } from 'actions/ConsumerPageActions';
import {
  getBaseFormFieldsInitialValues,
  useUpdateNamedConfigurations,
} from 'components/consumer-page-widgets/widgetFormUtils';
import { useAppDispatch } from 'state/hooks';
import { useSelector } from 'react-redux';
import {
  widgetFormButtonContainer,
  widgetFormRoot,
} from 'components/consumer-page-widgets/widgetFormCssDefs';
import React, { FunctionComponent } from 'react';

interface UpdateMetroAreaContainerWidgetFormProps {
  selectedWidgetToEdit?: consumer.NamedConfiguration;
  onCompleteOrCancel(): void;
}

function getInitialMetroWidgetValues(
  metroWidgets: consumer.IMetroAreaContainerWidgetItemConfig[],
  defaultWidgetNamedConfigurationId?: number | null,
  selectedWidgetToEdit?: consumer.NamedConfiguration
): MetroAreaContainerWidgetFields {
  return {
    ...getBaseFormFieldsInitialValues({ widgetToEdit: selectedWidgetToEdit }),
    defaultWidgetNamedConfigurationId: defaultWidgetNamedConfigurationId,
    widgets: metroWidgets,
  };
}

export const UpdateMetroAreaContainerWidgetForm: FunctionComponent<
  React.PropsWithChildren<UpdateMetroAreaContainerWidgetFormProps>
> = ({ selectedWidgetToEdit, onCompleteOrCancel }) => {
  const updateNamedConfig = useUpdateNamedConfigurations();

  const dispatch = useAppDispatch();

  const metroAreas = useSelector(
    (state: StoreType) => state.metroArea.metroAreaList
  ) as consumer.MetroArea[];

  React.useEffect(() => {
    dispatch(fetchAllNamedConfigurations());
    dispatch(fetchAllMetroAreas());
  }, [dispatch]);

  const activeNamedConfigs = useSelector(
    (state: StoreType) => state.consumerPage.namedConfigurations
  ) as consumer.NamedConfiguration[]; // TODO: Fix types on consumerPage state slice

  if (!selectedWidgetToEdit?.metroAreaWidgetConfig) {
    return null;
  }

  const marketingWidgets = activeNamedConfigs.filter((config) => {
    return (
      config.widgetType === 'widget-creator-widget' &&
      config.namedConfigurationConfig &&
      !invalidMetroWidgetItemTypes.includes(config.namedConfigurationConfig)
    );
  });

  const initialValues = getInitialMetroWidgetValues(
    selectedWidgetToEdit.metroAreaWidgetConfig?.metroWidgets ?? [],
    selectedWidgetToEdit.metroAreaWidgetConfig.defaultWidgetNamedConfigurationId,
    selectedWidgetToEdit
  );

  const handleSubmit = ({
    name,
    defaultWidgetNamedConfigurationId,
    widgets,
  }: MetroAreaContainerWidgetFields) => {
    return updateNamedConfig({
      ...selectedWidgetToEdit,
      name,
      metroAreaWidgetConfig: {
        defaultWidgetNamedConfigurationId,
        metroWidgets: widgets,
      },
    }).then(onCompleteOrCancel);
  };

  return (
    <>
      <Typography variant="heading1Bold">Editing widget: {selectedWidgetToEdit.name}</Typography>
      <Formik<MetroAreaContainerWidgetFields>
        enableReinitialize
        initialValues={{
          ...initialValues,
        }}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => (
          <Form css={widgetFormRoot}>
            <BaseWidgetFormFields />
            <MetroAreaContainerWidgetFormFields
              marketingWidgets={marketingWidgets}
              metroAreas={metroAreas}
              setFieldValue={setFieldValue}
            />
            <div css={widgetFormButtonContainer}>
              <Button type="submit">Submit</Button>
              <Button onClick={onCompleteOrCancel} type="button" variant="outlined">
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};
